import type { ReactElement, HTMLProps } from 'react';
import cx from 'clsx';
import {
  IconClockFilledSmall,
  IconPromoFilledSmall,
  IconActivateFilledSmall,
  IconTryFilledSmall,
  IconUpgradeFilledSmall,
  IconSaveFilledSmall,
  IconNotEligibleFilledSmall,
  IconSparklesOutlined,
  IconEinvoicingFilledSmall,
} from '@repo/monochrome-icons';
import styles from './badge.module.css';

type ValueOf<T> = T[keyof T];

export const BADGE_TYPE = {
  ACTIVATE: 'activate',
  NEW_FEATURE: 'newFeature',
  NOT_AVAILABLE: 'notAvailable',
  LIMITED_TIME: 'limitedTime',
  PROMO: 'promo',
  SAVE: 'save',
  TRY_FOR_FREE: 'tryForFree',
  UNION: 'union',
  UPGRADE: 'upgrade',
} as const;

export type BadgeType = ValueOf<typeof BADGE_TYPE>;

export interface BadgeProps extends HTMLProps<HTMLSpanElement> {
  type: BadgeType;
  text?: string;
  className?: string;
}
const BADGE_STYLE = {
  [BADGE_TYPE.ACTIVATE]: {
    IconComponent: IconActivateFilledSmall,
    colorClass: styles.purple,
    alt: 'activate',
  },
  [BADGE_TYPE.TRY_FOR_FREE]: {
    IconComponent: IconTryFilledSmall,
    colorClass: styles.mint,
    alt: 'try for free',
  },
  [BADGE_TYPE.UPGRADE]: {
    IconComponent: IconUpgradeFilledSmall,
    colorClass: styles.peach,
    alt: 'upgrade',
  },
  [BADGE_TYPE.SAVE]: { IconComponent: IconSaveFilledSmall, colorClass: styles.peach, alt: 'save' },
  [BADGE_TYPE.LIMITED_TIME]: {
    IconComponent: IconClockFilledSmall,
    colorClass: styles.limitedTime,
    alt: 'limitedTime',
  },
  [BADGE_TYPE.PROMO]: {
    IconComponent: IconPromoFilledSmall,
    colorClass: styles.promo,
    alt: 'promo',
  },
  [BADGE_TYPE.NOT_AVAILABLE]: {
    IconComponent: IconNotEligibleFilledSmall,
    colorClass: styles.primary,
    alt: 'notAvailable',
  },
  [BADGE_TYPE.NEW_FEATURE]: {
    IconComponent: IconSparklesOutlined,
    colorClass: styles.primary,
    alt: 'new feature',
  },
  [BADGE_TYPE.UNION]: {
    IconComponent: IconEinvoicingFilledSmall,
    colorClass: styles.purple,
    alt: 'union',
  },
} as const;

export function Badge({ type, text, className, ...props }: BadgeProps): ReactElement {
  const { IconComponent, colorClass, alt } = BADGE_STYLE[type];

  return (
    <span className={cx('caption', styles.badge, className)} data-test-badge={type} {...props}>
      <IconComponent
        aria-label={alt}
        className={cx(styles.icon, colorClass)}
        data-test-badge-icon={IconComponent.name}
        height={12}
        role="img"
        width={12}
      />
      {Boolean(text?.length) && (
        <span className={styles.textContainer}>
          <span>{text}</span>
        </span>
      )}
    </span>
  );
}
